export type AiIndexActivityKind = 'full_reindex' | 'note_reindex';

export interface AiIndexActivityEntry {
  kind: AiIndexActivityKind;
  timestamp: string;
  status: string;
  source?: string;
  jobId?: string;
  noteId?: string;
  noteTitle?: string;
  details?: string;
  error?: string;
  processed?: number;
  indexed?: number;
  skipped?: number;
  chunks?: number;
}

const STORAGE_KEY = 'ai-debug.index-activity-log.v1';
const MAX_ENTRIES = 100;

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function toFiniteNumber(value: unknown): number | undefined {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.floor(numeric) : undefined;
}

function sanitizeEntry(input: AiIndexActivityEntry): AiIndexActivityEntry {
  const timestamp = String(input.timestamp || '').trim() || new Date().toISOString();
  return {
    kind: input.kind,
    timestamp,
    status: String(input.status || '').trim() || 'unknown',
    source: input.source ? String(input.source).trim() : undefined,
    jobId: input.jobId ? String(input.jobId).trim() : undefined,
    noteId: input.noteId ? String(input.noteId).trim() : undefined,
    noteTitle: input.noteTitle ? String(input.noteTitle).trim() : undefined,
    details: input.details ? String(input.details).trim() : undefined,
    error: input.error ? String(input.error).trim() : undefined,
    processed: toFiniteNumber(input.processed),
    indexed: toFiniteNumber(input.indexed),
    skipped: toFiniteNumber(input.skipped),
    chunks: toFiniteNumber(input.chunks),
  };
}

export function readAiIndexActivityLog(): AiIndexActivityEntry[] {
  if (!hasLocalStorage()) {
    return [];
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((entry) => sanitizeEntry(entry as AiIndexActivityEntry))
      .filter((entry) => !!entry.kind && !!entry.timestamp);
  } catch {
    return [];
  }
}

export function writeAiIndexActivityLog(entries: AiIndexActivityEntry[]): void {
  if (!hasLocalStorage()) {
    return;
  }

  const normalized = entries
    .map((entry) => sanitizeEntry(entry))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, MAX_ENTRIES);

  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
}

export function appendAiIndexActivity(entry: AiIndexActivityEntry): void {
  const existing = readAiIndexActivityLog();
  writeAiIndexActivityLog([sanitizeEntry(entry), ...existing]);
}

export function getLatestAiIndexActivity(kind?: AiIndexActivityKind): AiIndexActivityEntry | null {
  const entries = readAiIndexActivityLog();
  if (!kind) {
    return entries[0] ?? null;
  }
  return entries.find((entry) => entry.kind === kind) ?? null;
}

export function getRecentAiIndexActivity(limit = 10): AiIndexActivityEntry[] {
  const safeLimit = Math.max(1, Math.floor(Number(limit) || 10));
  return readAiIndexActivityLog().slice(0, safeLimit);
}
